import {
  ExternalLink,
  FileText,
  Home,
  LayoutGrid,
  Package,
  Search,
  ShoppingCart,
  UserRound,
  type LucideIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { cn, formatEventTime, parseUTCDate } from '@/lib/utils'
import { isOnPageNow, pageLabel } from '@/features/storefront-chat/format'
import type { ChatPage, ChatPageView } from '@/features/storefront-chat/types'

/** A link to a page on our store (or a document we host); opens in a new tab. */
export function StoreLink({ href, className, children }: { href: string; className?: string; children: ReactNode }) {
  return (
    // eslint-disable-next-line no-restricted-syntax -- the store is a separate site
    <a href={href} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  )
}


const PAGE_ICONS: Record<ChatPage['page_type'] & string, LucideIcon> = {
  product: Package,
  collection: LayoutGrid,
  search: Search,
  cart: ShoppingCart,
  home: Home,
  account: UserRound,
  page: FileText,
  other: FileText,
}

export function PageIcon({ page, className }: { page: ChatPage; className?: string }) {
  const Icon = PAGE_ICONS[page.page_type ?? 'other'] ?? FileText
  return <Icon aria-hidden className={cn('h-3.5 w-3.5 shrink-0', className)} />
}

/** Where the shopper is now: the page, linked to the store. */
export function CurrentPage({
  page,
  seenAt,
  compact = false,
}: {
  page: ChatPage
  seenAt: string | null | undefined
  compact?: boolean
}) {
  const live = isOnPageNow(seenAt)
  return (
    <StoreLink
      href={page.url}
      className={cn(
        'group flex min-w-0 items-start gap-2.5 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
        compact ? 'p-0' : 'border bg-background p-2.5 hover:bg-muted/40',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
          live ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300' : 'bg-muted text-muted-foreground',
        )}
      >
        <PageIcon page={page} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
          {live ? (
            <>
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span className="text-emerald-700 dark:text-emerald-300">On this page now</span>
            </>
          ) : (
            seenAt && <>Last seen {formatEventTime(seenAt)}</>
          )}
        </span>
        <span
          className={cn(
            'block text-sm font-medium leading-snug text-foreground group-hover:underline',
            compact ? 'truncate' : 'line-clamp-3',
          )}
        >
          {pageLabel(page)}
        </span>
      </span>
      <ExternalLink aria-hidden className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
    </StoreLink>
  )
}

/** Every page the shopper opened while chatting, newest first. */
export function PageTrail({ pages }: { pages: ChatPageView[] }) {
  if (pages.length === 0) return null
  return (
    <ol aria-label="Pages visited" className="space-y-0.5">
      {pages.map((view, i) => (
        <li key={`${view.seen_at}-${i}`}>
          <StoreLink
            href={view.page.url}
            className="group/page flex items-start gap-2 rounded-md px-1.5 py-1.5 text-xs transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            <PageIcon page={view.page} className="mt-0.5 text-muted-foreground" />
            <span className="line-clamp-2 min-w-0 flex-1 leading-snug text-foreground group-hover/page:underline">
              {pageLabel(view.page)}
            </span>
            <time dateTime={view.seen_at} className="shrink-0 tabular-nums text-muted-foreground">
              {parseUTCDate(view.seen_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            </time>
          </StoreLink>
        </li>
      ))}
    </ol>
  )
}
